import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import {
  DropdownTabsController,
  type TabPanelLike,
  type DropdownTabsHost,
} from './dropdown-tabs.controller';

/**
 * Dropdown tabbed interface managing a list of `asc-tab-panel` elements.
 *
 * Renders a styled native `<select>` above an associated content region.
 * Enforces single-active-tab behavior and manages accessible markup.
 * Serves as the compact/mobile alternative to `asc-horizontal-tabs`
 * and `asc-vertical-tabs`, which no longer include built-in mobile
 * breakpoint behavior.
 *
 * All three tab widgets accept the same `asc-tab-panel` children, so
 * consumers can swap between them without duplicating content.
 *
 * @element asc-dropdown-tabs
 *
 * @fires asc-change - Fired after any tab activation. Detail: `{ activePanel: HTMLElement; activeIndex: number }`
 *
 * @slot - Default slot. Accepts `asc-tab-panel` elements only.
 *
 * @csspart base - Root vertical layout container.
 * @csspart select-wrapper - Wrapper positioning the chevron over the select.
 * @csspart select - The native `<select>` dropdown control.
 * @csspart chevron - Down-arrow SVG icon.
 * @csspart content - Panel projection area wrapping the default slot.
 *
 * @cssprop --asc-dropdown-tabs-gap - Gap between select and content. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-dropdown-tabs-content-bg - Content area background. Default: `transparent`
 * @cssprop --asc-dropdown-tabs-select-bg - Select background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-dropdown-tabs-select-border-color - Select border color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-dropdown-tabs-select-border-radius - Select border radius. Default: `var(--asc-radius-md)`
 * @cssprop --asc-dropdown-tabs-select-text-color - Select text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-dropdown-tabs-select-padding-x - Select horizontal padding. Default: `14px`
 * @cssprop --asc-dropdown-tabs-select-padding-y - Select vertical padding. Default: `10px`
 * @cssprop --asc-dropdown-tabs-chevron-color - Chevron icon color. Default: `var(--asc-text-primary)`
 */
@customElement('asc-dropdown-tabs')
export class AscDropdownTabs extends LitElement implements DropdownTabsHost {
  static override styles = css`
    :host {
      display: block;
    }

    /* ── Base vertical layout ─────────────────────────────────── */
    [part='base'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-dropdown-tabs-gap, var(--asc-spacing-3xl));
    }

    /* ── Select wrapper ───────────────────────────────────────── */
    [part='select-wrapper'] {
      position: relative;
      display: flex;
      align-items: center;
    }

    /* ── Native select ────────────────────────────────────────── */
    [part='select'] {
      appearance: none;
      -webkit-appearance: none;
      width: 100%;
      background: var(--asc-dropdown-tabs-select-bg, var(--asc-bg-primary));
      border: var(--asc-border-width, 1px) solid
        var(--asc-dropdown-tabs-select-border-color, var(--asc-border-primary));
      border-radius: var(--asc-dropdown-tabs-select-border-radius, var(--asc-radius-md));
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
      color: var(--asc-dropdown-tabs-select-text-color, var(--asc-text-primary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-md);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-md);
      padding: var(--asc-dropdown-tabs-select-padding-y, 10px)
        calc(var(--asc-dropdown-tabs-select-padding-x, 14px) + 20px)
        var(--asc-dropdown-tabs-select-padding-y, 10px)
        var(--asc-dropdown-tabs-select-padding-x, 14px);
      cursor: pointer;
      margin: 0;
    }

    [part='select']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* ── Chevron icon ─────────────────────────────────────────── */
    [part='chevron'] {
      position: absolute;
      right: var(--asc-dropdown-tabs-select-padding-x, 14px);
      pointer-events: none;
      color: var(--asc-dropdown-tabs-chevron-color, var(--asc-text-primary));
      width: 12px;
      height: 12px;
      flex-shrink: 0;
    }

    /* ── Content area ─────────────────────────────────────────── */
    [part='content'] {
      background: var(--asc-dropdown-tabs-content-bg, transparent);
    }

    /* ── Hidden utility ───────────────────────────────────────── */
    [hidden] {
      display: none !important;
    }
  `;

  private _controller = new DropdownTabsController(this);

  /** Accessible label for the select element. */
  @property({ type: String }) label = '';

  @state() private _panels: TabPanelLike[] = [];

  private _syncPanels(): void {
    this._panels = this._controller.syncPanels();
  }

  private _activatePanel(index: number): void {
    const panels = this._panels;
    if (!panels[index] || panels[index].disabled) return;
    panels.forEach((p, i) => { p.active = i === index; });
    this._panels = [...panels];
    dispatch<{ activePanel: HTMLElement; activeIndex: number }>(
      this, 'asc-change', { detail: { activePanel: panels[index], activeIndex: index } },
    );
  }

  private _handleSlotChange(): void {
    this._syncPanels();
  }

  private _handleAscActivate(e: Event): void {
    const target = e.target as TabPanelLike;
    const index = this._panels.indexOf(target);
    if (index !== -1) this._activatePanel(index);
  }

  private _handleSelectChange(e: Event): void {
    const select = e.target as HTMLSelectElement;
    const index = Number(select.value);
    if (!Number.isNaN(index)) {
      this._activatePanel(index);
    }
  }

  private _getActiveIndex(): number {
    return this._panels.findIndex(p => p.active);
  }

  /** Builds the option label: tab name + optional count in parentheses. */
  private _getOptionLabel(panel: TabPanelLike): string {
    const name = this._controller.getTabNameText(panel);
    const count = this._controller.getCountText(panel);
    return count ? `${name} (${count})` : name;
  }

  override render() {
    const activeIndex = this._getActiveIndex();

    return html`
      <div part="base">
        <div part="select-wrapper">
          <select
            part="select"
            aria-label=${this.label || 'Select tab'}
            @change=${this._handleSelectChange}
          >
            ${this._panels.map((panel, i) => html`
              <option
                value=${i}
                ?selected=${i === activeIndex}
                ?disabled=${panel.disabled}
              >${this._getOptionLabel(panel)}</option>
            `)}
          </select>
          <svg
            part="chevron"
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <section part="content">
          <slot
            @slotchange=${this._handleSlotChange}
            @asc-activate=${this._handleAscActivate}
          ></slot>
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-dropdown-tabs': AscDropdownTabs;
  }
}
